import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import usePageContent from '@/lib/usePageContent';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const supportTopics = [
  {
    title: 'Artist verification',
    text: 'Questions about profile approval, featured status, portfolio links, images, or videos.',
  },
  {
    title: 'Collaborator support',
    text: 'Help with collaborator profiles, enquiries, PMB conversations, or hiring workflow.',
  },
  {
    title: 'Profile correction',
    text: 'Request correction or removal of incorrect profile details, contact information, or media.',
  },
  {
    title: 'Safety and media review',
    text: 'Report adult, unsafe, misleading, copied, or inappropriate images, videos, or messages.',
  },
];

const Contact = () => {
  const [searchParams] = useSearchParams();
  const subjectFromUrl = searchParams.get('subject') || '';
  const messageFromUrl = searchParams.get('message') || '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const pageContent = usePageContent('contact');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const contactPayload = {
      name: formData.get('name') || '',
      email: formData.get('email') || '',
      subject: formData.get('subject') || 'Kala Manch contact form message',
      message: formData.get('message') || '',
      page_url: window.location.href,
    };
    const subject = formData.get('subject') || 'Kala Manch contact form message';
    formData.append('_subject', `Kala Manch: ${subject}`);
    formData.append('_template', 'table');

    setIsSubmitting(true);
    setSubmitStatus(null);
    trackEvent('contact_form_submit', { subject });

    try {
      const backendResponse = await fetch(`${API}/contact/messages`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactPayload),
      });

      if (!backendResponse.ok) {
        const errorData = await backendResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Unable to save message');
      }

      await fetch('https://formsubmit.co/ajax/me@kalamanch.art', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }).catch(() => null);

      const thankYouMessage = 'Thank you for your message. We have received it at me@kalamanch.art and will reply quickly, especially for urgent concerns.';
      setSubmitStatus({ type: 'success', text: thankYouMessage });
      trackEvent('contact_form_success', { subject });
      alert(thankYouMessage);
      form.reset();
    } catch (error) {
      trackEvent('contact_form_error', { subject });
      setSubmitStatus({
        type: 'error',
        text: error.message || 'We could not send the message from the form right now. Please email me@kalamanch.art directly for urgent support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50 pt-24" data-testid="contact-page">
      <SEO
        title="Contact Kala Manch"
        description="Contact Kala Manch for artist verification, collaborator onboarding, profile correction, media review, support, and safety concerns."
        keywords="Kala Manch contact, Kalamanch support, artist verification support, collaborator support"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00] mb-4">Support center</p>
          <h1 className="launch-heading mx-auto max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6" data-testid="contact-heading">
            {pageContent.hero_title || 'Contact Kala Manch'}
          </h1>
          <p className="launch-copy text-lg text-gray-600 max-w-3xl mx-auto" data-testid="contact-subtitle">
            {pageContent.hero_subtitle || 'For artist verification, collaborator onboarding, profile corrections, media removal, or technical help, email us and include the profile URL if available.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover-lift" data-testid="contact-card-email">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6F00] to-[#FF8F00] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Email</h3>
            <p className="text-base text-gray-600">{pageContent.email || 'me@kalamanch.art'}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover-lift" data-testid="contact-card-location">
            <div className="w-16 h-16 bg-gradient-to-br from-[#00BFA5] to-[#00D9B5] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Location</h3>
            <p className="text-base text-gray-600">{pageContent.location || 'Ghaziabad, India'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {supportTopics.map((topic) => (
            <div key={topic.title} className="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-orange-100">
              <h3 className="text-lg font-bold text-gray-900">{topic.title}</h3>
              <p className="mt-3 text-sm leading-6 text-gray-600">{topic.text}</p>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold gradient-text mb-3 text-center" data-testid="form-heading">
              {pageContent.form_heading || 'Send Us a Message'}
            </h2>
            <p className="text-center text-gray-600 mb-8">
              {pageContent.form_description || 'Mail us at:'} <span className="font-semibold">{pageContent.email || 'me@kalamanch.art'}</span>
            </p>
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input type="text" id="name" name="name" data-testid="form-input-name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all text-base" placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" data-testid="form-input-email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all text-base" placeholder="you@example.com" />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input type="text" id="subject" name="subject" data-testid="form-input-subject" required defaultValue={subjectFromUrl} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all text-base" placeholder="Artist verification, profile correction, media removal..." />
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea id="message" name="message" data-testid="form-input-message" required rows="6" defaultValue={messageFromUrl} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all resize-none text-base" placeholder="Tell us more. If this is about a profile, include the profile link."></textarea>
              </div>
              <button type="submit" data-testid="form-submit-button" className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-70" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus && (
                <div className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
                  submitStatus.type === 'success'
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}>
                  {submitStatus.text}
                </div>
              )}
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;
